import { imgSrcSet } from "../lib/media";
import { recipeSrcSet } from "../lib/media";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ShoppingBasket, Users, ArrowRight, ArrowLeft, Save, Share2, Check, BookOpen, Clock, Trash2, Repeat, Info, Coins, Loader2, Minus, Plus, MessageCircle, ExternalLink, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { CategoryBudget } from "../components/CategoryBudget";
import { Modal } from "../components/Modal";
import { MenuShoppingList } from "../components/MenuShoppingList";
import { SaveName } from "../components/SaveName";
import { PageTutorial } from "../components/PageTutorial";
import { SaveChangesBar } from "../components/SaveChangesBar";
import { WhatsAppShareButton, openWhatsApp } from "../components/WhatsAppShare";
import { CEIA_TUTORIAL } from "../tutorials/ceia";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { PageHeader, Loader } from "../components/shared";
import ProductResults from "../components/ProductResults";
import { ProductSearchBar } from "../components/ProductSearchBar";

const GUEST_OPTIONS = [2, 4, 6, 8, 10, 15, 20];
const LANG_MENU_COUNTRY = { pt: "BR", "pt-PT": "PT", en: "US", es: "ES", fr: "FR", it: "IT" };

const COURSES = [
  { key: "starters", cats: ["starters"] },
  { key: "main", cats: ["meats", "poultry", "fish"] },
  { key: "sides", cats: ["sides"] },
  { key: "dessert", cats: ["desserts"] },
  { key: "drink", cats: ["drinks"] },
];

export default function Ceia() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const [styles, setStyles] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [recipeModal, setRecipeModal] = useState(null);
  const [swapModal, setSwapModal] = useState(null);
  const [saves, setSaves] = useState([]);
  const [saveView, setSaveView] = useState(null);
  const [savedDirty, setSavedDirty] = useState(false);
  const [params] = useSearchParams();
  const wantedSave = params.get("save");
  useEffect(() => {
    if (!wantedSave) return;
    const sv = saves.find((x) => x.save_id === wantedSave);
    if (sv) setSaveView(sv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedSave, saves.length]);
  const [prices, setPrices] = useState({});
  const [priceCurrency, setPriceCurrency] = useState(null);
  const [pricesState, setPricesState] = useState("idle");
  const [priceCountry, setPriceCountry] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [extras, setExtras] = useState([]);
  const [prodModal, setProdModal] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [step, setStep] = useState(1);
  const [guests, setGuests] = useState(8);
  const [budget, setBudget] = useState(null);
  const [style, setStyle] = useState(null);
  const [typicalMenus, setTypicalMenus] = useState({});
  const [menuList, setMenuList] = useState(false);
  // Market whose typical menu is featured: the user's country, else the country implied by the UI language.
  const menuCountry = [user?.country, LANG_MENU_COUNTRY[lang]].find((c) => c && typicalMenus[c]) || null;

  useEffect(() => {
    setPrices({});
    setPricesState("idle");
    api.get(`/content/ceia?lang=${lang}`).then(({ data }) => { setStyles(data.styles); setTypicalMenus(data.typical_menus || {}); }).catch(() => {});
    api.get(`/content/recipes?lang=${lang}`).then(({ data }) => setRecipes(data.recipes || [])).catch(() => {});
  }, [lang]);

  useEffect(() => {
    api.get("/ceia/saves").then(({ data }) => setSaves(data.saves || [])).catch(() => {});
    // Default the guest count to what the user defined in "Criar Meu Natal".
    api.get("/christmas").then(({ data }) => { const g = parseInt(data?.plan?.guests, 10); if (g >= 1) setGuests(g); }).catch(() => {});
  }, []);

  useEffect(() => {
    // Reuse the already-defined category budget (single source of truth) instead of asking again.
    api.get("/budget").then(({ data }) => {
      const lim = data.budget?.categories?.ceia?.limit;
      if (lim != null) setBudget(lim);
    }).catch(() => {});
  }, []);

  if (!styles) return <Loader />;
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const estSymbol = symbolForCurrency(config, priceCurrency || user?.currency || "BRL");
  const fmtPrice = (v) => `~${formatMoney(estSymbol, v)}`;
  const label = (obj) => (obj && (obj[lang] || obj.pt)) || obj;
  const selectedRecipes = recipes.filter((r) => selected.includes(r.id));
  const pricedSelected = selectedRecipes.filter((r) => prices[r.id] != null);
  const extrasTotal = extras.reduce((acc, x) => acc + Number(x.est_price || 0) * Number(x.qty || 1), 0);
  // Google Shopping price is for the dish as published (r.servings people) → scale to the chosen guests.
  const scaleFor = (r, g = guests) => (r.servings && g ? Number(g) / Number(r.servings) : 1);
  const scaledPrice = (r, g = guests) => (prices[r.id] != null ? Math.round(Number(prices[r.id]) * scaleFor(r, g) * 100) / 100 : null);
  const perPerson = (r) => (prices[r.id] != null && r.servings ? Math.round((Number(prices[r.id]) / Number(r.servings)) * 100) / 100 : null);
  const liveTotal = Math.round((pricedSelected.reduce((acc, r) => acc + scaledPrice(r), 0) + extrasTotal) * 100) / 100;
  const dishesTotal = (dishes) => Math.round((dishes || []).reduce((acc, d) => acc + Number(d.est_price ?? prices[d.id] ?? 0) * Number(d.qty || 1), 0) * 100) / 100;
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const courseFor = (r) => COURSES.find((c) => c.cats.includes(r?.category));
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const swapEffStyle = () => (saveView ? saveView.style : style);
  const swapAlternatives = swapModal ? recipes.filter((r) => courseFor(swapModal)?.cats.includes(r.category) && r.id !== swapModal.id && (r.styles || []).includes(swapEffStyle())) : [];
  const altsFor = (r) => r ? recipes.filter((x) => courseFor(r)?.cats.includes(x.category) && x.id !== r.id && (x.styles || []).includes(saveView ? saveView.style : style)) : [];


  const computeEstimate = async (list) => {
    const dishes = list.map((r) => ({ id: r.id, name: r.name }));
    const { data } = await api.post("/ceia/estimate", { lang, dishes });
    if (data.state === "ok") {
      setPrices((p) => ({ ...p, ...(data.estimates || {}) }));
      if (data.currency) setPriceCurrency(data.currency);
      if (data.country) setPriceCountry(data.country);
    }
    return data;
  };

  const doSwap = async (newR) => {
    const oldId = swapModal.id;
    if (saveView) {
      let price = prices[newR.id];
      if (price === undefined) {
        setEstimating(true);
        try { const est = await computeEstimate([newR]); price = est?.estimates?.[newR.id] ?? null; } catch { price = null; }
        setEstimating(false);
      }
      const g = saveView.guests || guests;
      const scaled = price != null ? Math.round(Number(price) * scaleFor(newR, g) * 100) / 100 : null;
      const newDishes = (saveView.dishes || []).map((d) =>
        d.id === oldId ? { id: newR.id, name: newR.name, image: newR.image, category: newR.category, est_price: scaled, unit_price: price, servings: newR.servings || null } : d
      );
      const updated = {
        ...saveView, dishes: newDishes,
        estimate_total: null,
        estimate_currency: priceCurrency || saveView.estimate_currency,
      };
      setSwapModal(null);
      setRecipeModal(newR);
      setSaveView(updated);
      if (price != null) toast.success(`${newR.name}: ${fmtPrice(scaled)}`);
      try {
        const { data } = await api.put(`/ceia/saves/${saveView.save_id}`, {
          style: saveView.style, guests: saveView.guests, budget: saveView.budget, name: saveView.name ?? null,
          dishes: newDishes, estimate_total: updated.estimate_total, estimate_currency: updated.estimate_currency,
        });
        setSaves(data.saves || []);
      } catch { toast.error("Error"); }
      return;
    }
    setSelected((s) => [...s.filter((x) => x !== oldId && x !== newR.id), newR.id]);
    setSwapModal(null);
    setRecipeModal(newR);
  };

  const save = async () => {
    if (!selectedRecipes.length && !extras.length) { toast.error(t("ceia.no_selection")); return; }
    if (saves.length >= 3) { toast.error(t("ceia.saves_full")); return; }
    const missing = selectedRecipes.filter((r) => prices[r.id] === undefined);
    let extra = {};
    if (missing.length) {
      setEstimating(true);
      try { const est = await computeEstimate(missing); extra = est?.estimates || {}; } catch { /* keep prior */ }
      setEstimating(false);
    }
    const priceOf = (id) => prices[id] ?? extra[id] ?? null;
    const scaledOf = (r) => (priceOf(r.id) != null ? Math.round(Number(priceOf(r.id)) * scaleFor(r) * 100) / 100 : null);
    const dishes = [...selectedRecipes.map((r) => ({ id: r.id, name: r.name, image: r.image, category: r.category, est_price: scaledOf(r), unit_price: priceOf(r.id), servings: r.servings || null })), ...extras];
    try {
      await api.post("/meal-plans", { guests, budget, style, dishes: selectedRecipes.map((r) => ({ id: r.id, name: r.name })) });
      const { data } = await api.post("/ceia/saves", {
        style, guests, budget, dishes, name: saveName.trim() || null,
        estimate_total: null, estimate_currency: priceCurrency || null,
      });
      setSaves(data.saves || []);
      setSelected([]);
      setExtras([]);
      setSaveName("");
      toast.success(t("ceia.saved"));
    } catch { toast.error("Error"); }
  };
  const deleteSave = async (id) => {
    try { const { data } = await api.delete(`/ceia/saves/${id}`); setSaves(data.saves || []); } catch { /* ignore */ }
  };
  const editSaved = (dishes) => { setSaveView({ ...saveView, dishes, estimate_total: null }); setSavedDirty(true); };
  const setSavedQty = (i, qty) => editSaved((saveView.dishes || []).map((d, j) => j === i ? { ...d, qty } : d));
  const removeSavedDish = (i) => editSaved((saveView.dishes || []).filter((_, j) => j !== i));
  const saveSavedChanges = async () => {
    try {
      const { data } = await api.put(`/ceia/saves/${saveView.save_id}`, {
        style: saveView.style, guests: saveView.guests, budget: saveView.budget, dishes: saveView.dishes || [], name: saveView.name ?? null,
        estimate_total: null, estimate_currency: saveView.estimate_currency || priceCurrency || null,
      });
      setSaves(data.saves || []);
      setSavedDirty(false);
      toast.success(t("saves.updated"));
    } catch { toast.error("Error"); }
  };
  const discardSavedChanges = () => { setSaveView(saves.find((x) => x.save_id === saveView?.save_id) || null); setSavedDirty(false); };
  const renameSave = async (sv, name) => {
    try {
      const { data } = await api.put(`/ceia/saves/${sv.save_id}`, {
        style: sv.style, guests: sv.guests, budget: sv.budget, dishes: sv.dishes || [],
        estimate_total: sv.estimate_total, estimate_currency: sv.estimate_currency, name,
      });
      setSaves(data.saves || []);
      toast.success(t("saves.updated"));
    } catch { toast.error("Error"); }
  };
  const goResult = async () => {
    try {
      const { data } = await api.get("/budget");
      const lim = data.budget?.categories?.ceia?.limit;
      if (lim != null) setBudget(lim);
    } catch { /* keep current */ }
    // Keep only dishes that belong to the chosen style
    setSelected((s) => s.filter((id) => (recipes.find((r) => r.id === id)?.styles || []).includes(style)));
    setStep(4);
  };
  const shareSavedWhatsApp = (sv) => {
    const lines = [`🎄 *${sv.name || t("ceia.saved_items")}* · ${sv.guests} ${t("ceia.guests")}`, ""];
    (sv.dishes || []).forEach((d) => {
      const q = Number(d.qty || 1);
      lines.push(`• ${d.name}${q > 1 ? ` ×${q}` : ""}`);
    });
    lines.push("", "_MY CHRISTMAS_");
    openWhatsApp(lines.join("\n"));
  };

  const sameCurrency = !priceCurrency || priceCurrency === (user?.currency || "BRL");
  const overBy = sameCurrency && budget != null && liveTotal > Number(budget) ? Math.round((liveTotal - Number(budget)) * 100) / 100 : 0;
  const searchCountry = priceCountry || user?.country || "BR";
  const openProductSearch = async (q, target) => {
    const room = target === "draft" && sameCurrency && budget != null ? Math.max(0, Number(budget) - liveTotal) : (target === "saved" && saveView?.budget ? Math.max(0, Number(saveView.budget) - dishesTotal(saveView.dishes)) : null);
    setProdModal({ label: q, target, loading: true, result: null });
    try {
      const maxP = room != null && room >= 1 ? `&max_price=${Math.round(room)}` : "";
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${searchCountry}${maxP}&limit=10&min_results=8&diverse=1`);
      setProdModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setProdModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const chooseExtra = (p) => {
    const item = { id: `extra_${Date.now()}`, name: p.title, image: p.thumbnail || null, category: "extra", est_price: p.extracted_price ?? null, qty: 1, link: p.product_link || null, store: p.source || null };
    if (prodModal?.target === "saved" && saveView) editSaved([...(saveView.dishes || []), item]);
    else setExtras((x) => [...x, item]);
    if (p.extracted_price != null && priceCurrency == null) setPriceCurrency(user?.currency || "BRL");
    setProdModal(null);
    toast.success(t("psearch.ceia_added"));
  };
  const setExtraQty = (id, qty) => setExtras((x) => x.map((e) => e.id === id ? { ...e, qty } : e));
  const removeExtra = (id) => setExtras((x) => x.filter((e) => e.id !== id));
  const loadSave = (s) => {
    setStyle(s.style);
    if (s.guests) setGuests(s.guests);
    setSelected((s.dishes || []).map((d) => d.id).filter((id) => recipes.some((r) => r.id === id)));
    setExtras((s.dishes || []).filter((d) => !recipes.some((r) => r.id === d.id)).map((d) => ({ ...d, qty: Number(d.qty || 1) })));
    setSaveView(null);
    setRecipeModal(null);
    setStep(4);
    toast.success(t("ceia.loaded"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const savesPanel = (
    <div data-testid="ceia-saves">
      <div className="mb-1 flex items-center gap-2">
        <Clock className="h-4 w-4 text-xmas-gold" />
        <h3 className="font-display text-sm text-xmas-snow">{t("ceia.saves_title")}</h3>
        <span className="text-[11px] text-xmas-slate">({saves.length}/3)</span>
      </div>
      <p className="mb-3 text-xs text-xmas-slate">{t("ceia.saves_hint")}</p>
      {saves.length === 0 ? (
        <p className="text-xs text-xmas-slate">{t("ceia.saves_empty")}</p>
      ) : (
        <div className="space-y-2">
          {saves.map((s) => (
            <div key={s.save_id} data-testid={`ceia-save-${s.save_id}`} className="flex items-center justify-between gap-2 rounded-xl bg-xmas-navy/40 px-3 py-2">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <button onClick={() => setSaveView(s)} data-testid={`ceia-save-view-${s.save_id}`} aria-label="view" className="shrink-0"><Clock className="h-3.5 w-3.5 text-xmas-gold" /></button>
                <SaveName name={s.name} fallback={fmtDate(s.saved_at)} placeholder={t("saves.name_ph")} testid={`ceia-save-name-${s.save_id}`} onClick={() => setSaveView(s)} onRename={(v) => renameSave(s, v)} />
                {s.name && <span className="shrink-0 text-[11px] text-xmas-slate">{fmtDate(s.saved_at)}</span>}
                <button onClick={() => setSaveView(s)} className="shrink-0 font-mono-num text-xs text-xmas-champagne">{t("ceia.selected_count").replace("{n}", s.count ?? (s.dishes || []).length)}</button>
              </div>
              <button onClick={() => deleteSave(s.save_id)} data-testid={`ceia-save-delete-${s.save_id}`} aria-label="delete" className="shrink-0 rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDish = (r) => {
    const isSel = selected.includes(r.id);
    return (
      <div key={r.id} data-testid={`ceia-dish-${r.id}`}
              className={`flex flex-col overflow-hidden rounded-xl border transition ${isSel ? "border-xmas-gold ring-2 ring-xmas-gold/40" : "border-xmas-gold/20 hover:border-xmas-gold/50"}`}>
              <button onClick={() => toggleSelect(r.id)} data-testid={`ceia-dish-select-${r.id}`} className="relative block text-left">
                <div className="relative h-28 w-full">
                  <img src={r.image} srcSet={recipeSrcSet(r.image)} sizes="(max-width: 640px) 100vw, 360px" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  {isSel && <div className="absolute right-2 top-2 rounded-full bg-xmas-gold p-1"><Check className="h-3.5 w-3.5 text-xmas-navy" /></div>}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-xmas-snow">{r.name}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-xmas-slate">{r.description}</p>
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-xmas-champagne">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {r.time_minutes} min</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {r.servings}</span>
                    </div>
                  </div>
                </div>
              </button>
              <button onClick={() => setRecipeModal(r)} data-testid={`ceia-dish-recipe-${r.id}`}
                className="mt-auto flex items-center justify-center gap-1.5 border-t border-xmas-gold/15 px-3 py-2 text-xs font-semibold text-xmas-gold hover:bg-xmas-gold/10">
                <BookOpen className="h-3.5 w-3.5" /> {t("ceia.view_recipe")}
              </button>
            </div>
    );
  };
  const typicalMenu = menuCountry ? typicalMenus[menuCountry] : null;
  const consoada = menuCountry ? recipes.filter((r) => r.typical?.[menuCountry]).sort((a, b) => a.typical[menuCountry] - b.typical[menuCountry]) : [];
  const allTypicalSelected = consoada.length > 0 && consoada.every((r) => selected.includes(r.id));
  const addConsoada = () => {
    const ids = consoada.map((r) => r.id);
    if (allTypicalSelected) {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
      toast.success(t("ceia.typical_removed"));
      return;
    }
    setSelected((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
    toast.success(t("ceia.typical_added"));
  };

  return (
    <div>
      <PageHeader title={t("ceia.title")} testid="ceia-header" />
      <PageTutorial id="ceia" content={CEIA_TUTORIAL} />
      <CategoryBudget category="ceia" aside={savesPanel} />

      {step === 1 && (
        <div className="glass rounded-2xl p-6 sm:p-8" data-testid="ceia-step-guests">
          <h2 className="mb-6 flex items-center gap-2 font-display text-xl text-xmas-snow">
            <Users className="h-5 w-5 text-xmas-gold" /> {t("ceia.guests_q")}
          </h2>
          <div className="flex flex-wrap gap-3">
            {GUEST_OPTIONS.map((g) => (
              <button
                key={g}
                data-testid={`ceia-guest-${g}`}
                onClick={() => setGuests(g)}
                className={`h-14 w-14 rounded-full text-lg font-semibold transition ${guests === g ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2" data-testid="ceia-guests-custom">
            <span className="text-sm text-xmas-slate">{t("wiz.guests_other")}</span>
            <input type="number" min={1} max={500} inputMode="numeric" data-testid="ceia-guests-input"
              value={GUEST_OPTIONS.includes(guests) ? "" : guests}
              placeholder={t("wiz.guests_ph")}
              onChange={(e) => { const v = parseInt(e.target.value, 10); if (e.target.value === "") return; if (v >= 1 && v <= 500) setGuests(v); }}
              className={`w-24 rounded-full border px-3 py-2 text-center text-sm outline-none focus:border-xmas-gold ${!GUEST_OPTIONS.includes(guests) ? "border-xmas-gold bg-xmas-gold/15 text-xmas-snow" : "border-xmas-gold/20 bg-xmas-navy/50 text-xmas-snow"}`} />
            <span className="text-sm text-xmas-slate">{t("wiz.people")}</span>
          </div>
          <button onClick={() => setStep(3)} data-testid="ceia-next-1" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
            {t("cta.continue")} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="glass rounded-2xl p-6 sm:p-8" data-testid="ceia-step-style">
          <h2 className="mb-6 font-display text-xl text-xmas-snow">{t("ceia.style_q")}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {styles.map((s) => (
              <button
                key={s.id}
                data-testid={`ceia-style-${s.id}`}
                onClick={() => setStyle(s.id)}
                className={`overflow-hidden rounded-xl border text-left transition ${style === s.id ? "border-xmas-gold" : "border-xmas-gold/15 hover:border-xmas-gold/50"}`}
              >
                <img src={s.img} srcSet={imgSrcSet(s.img)} sizes="(max-width: 640px) 100vw, 320px" alt={s.name} loading="lazy" decoding="async" className="h-32 w-full object-cover" />
                <div className="p-4">
                  <h3 className="font-display text-lg text-xmas-snow">{s.name}</h3>
                  <p className="mt-1 text-sm text-xmas-slate">{s.desc}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <button onClick={() => setStep(1)} className="rounded-full border border-xmas-gold/30 px-6 py-3 text-sm text-xmas-slate hover:text-xmas-snow">{t("cta.back")}</button>
            <button onClick={goResult} disabled={!style} data-testid="ceia-generate" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
              {t("cta.continue")} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div data-testid="ceia-result">
          <button onClick={() => setStep(3)} data-testid="ceia-back-top" className="mb-4 inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-4 py-2 text-sm text-xmas-snow transition hover:border-xmas-gold">
            <ArrowLeft className="h-4 w-4" /> {t("cta.back")}
          </button>

          <div className="glass rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1" data-testid="ceia-result-head">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-2xl text-xmas-gold">{t("ceia.choose_title")}</h2>
                  <span className="rounded-full bg-xmas-gold/15 px-3 py-1 text-xs font-semibold text-xmas-gold" data-testid="ceia-selected-count">{t("ceia.selected_count").replace("{n}", selected.length)}</span>
                </div>
                <p className="mt-1 text-sm text-xmas-slate">{t("ceia.choose_sub")}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-xmas-slate">
                  <span><b className="font-mono-num text-xmas-snow">{guests}</b> {t("ceia.guests")}</span>
                  {budget != null && <span className="font-mono-num">{formatMoney(symbol, budget)}</span>}
                  <span className="capitalize text-xmas-champagne">{styles.find((s) => s.id === style)?.name}</span>
                </div>
              </div>
            </div>

            {consoada.length > 0 && (
              <div className="mt-6 rounded-2xl border border-xmas-gold/40 bg-gradient-to-br from-xmas-gold/15 via-transparent to-emerald-900/20 p-5 sm:p-6" data-testid="ceia-consoada">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/40 bg-xmas-navy/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-xmas-gold" data-testid="ceia-typical-badge">{typicalMenu.flag} {typicalMenu.badge}</span>
                    <h3 className="mt-2 font-display text-2xl text-xmas-snow" data-testid="ceia-typical-title">{typicalMenu.title}</h3>
                    <p className="mt-1 max-w-2xl text-sm text-xmas-slate">{typicalMenu.sub}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={addConsoada} data-testid="ceia-consoada-add-all" aria-pressed={allTypicalSelected}
                      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider ${allTypicalSelected ? "border border-xmas-gold/50 bg-xmas-navy/60 text-xmas-gold hover:bg-red-900/30 hover:border-red-400/50 hover:text-red-200" : "btn-gold"}`}>
                      {allTypicalSelected ? <Minus className="h-4 w-4" /> : <Check className="h-4 w-4" />} {allTypicalSelected ? t("ceia.typical_remove_all") : t("ceia.typical_add_all")}
                    </button>
                    <button onClick={() => setMenuList(true)} data-testid="ceia-menu-list-btn" className="glass inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-xmas-snow hover:text-xmas-gold">
                      <ShoppingBasket className="h-4 w-4" /> {t("ceia.list_btn")}
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {consoada.map(renderDish)}
                </div>
              </div>
            )}

            <div className="mt-6 space-y-7">
              {COURSES.map((course) => {
                const items = recipes.filter((r) => course.cats.includes(r.category) && (r.styles || []).includes(style));
                if (!items.length) return null;
                return (
                  <div key={course.key} data-testid={`ceia-course-${course.key}`}>
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-xmas-champagne">{t(`ceia.course.${course.key}`)}</h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map(renderDish)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8">
              <ProductSearchBar historyKey="ceia" testid="ceia-search" onSearch={(q) => openProductSearch(q, "draft")}
                maxLabel={sameCurrency && budget != null && Number(budget) - liveTotal > 0 ? formatMoney(estSymbol, Math.round((Number(budget) - liveTotal) * 100) / 100) : null} />
            </div>

            {(selected.length > 0 || extras.length > 0) && (
              <div className="mt-6 rounded-2xl border border-xmas-gold/25 bg-xmas-gold/10 p-5" data-testid="ceia-estimate-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-5 w-5 text-xmas-gold" />
                    <h3 className="font-display text-lg text-xmas-snow">{t("ceia.selected_count").replace("{n}", selected.length)}</h3>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {selectedRecipes.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-xmas-navy/40 px-3 py-2" data-testid={`ceia-estimate-row-${r.id}`}>
                      <span className="truncate text-sm text-xmas-snow">{r.name}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        <button type="button" onClick={() => toggleSelect(r.id)} data-testid={`ceia-estimate-remove-${r.id}`} aria-label="remove"
                          className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                  ))}
                  {extras.map((x) => (
                    <div key={x.id} className="flex items-center justify-between gap-2 rounded-lg border border-xmas-gold/20 bg-xmas-navy/40 px-3 py-2" data-testid={`ceia-extra-row-${x.id}`}>
                      <span className="flex min-w-0 items-center gap-2">
                        {x.image && <img src={x.image} alt="" className="h-8 w-8 shrink-0 rounded-md bg-white object-contain" />}
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-xmas-snow">{x.name}</span>
                          <span className="block text-[11px] text-xmas-slate">{t("psearch.extra_label")}{x.store ? ` · ${x.store}` : ""}</span>
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 px-1.5 py-0.5 text-xs">
                          <button type="button" onClick={() => setExtraQty(x.id, Math.max(1, x.qty - 1))} disabled={x.qty <= 1} data-testid={`ceia-extra-qty-minus-${x.id}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20 disabled:opacity-30"><Minus className="h-3 w-3" /></button>
                          <span className="font-mono-num min-w-[1.25rem] text-center text-xmas-snow" data-testid={`ceia-extra-qty-${x.id}`}>{x.qty}</span>
                          <button type="button" onClick={() => setExtraQty(x.id, x.qty + 1)} data-testid={`ceia-extra-qty-plus-${x.id}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20"><Plus className="h-3 w-3" /></button>
                        </span>
                        <span className="font-mono-num text-sm text-xmas-champagne" data-testid={`ceia-extra-price-${x.id}`}>{x.est_price != null ? fmtPrice(Math.round(x.est_price * x.qty * 100) / 100) : "—"}</span>
                        <button type="button" onClick={() => removeExtra(x.id)} data-testid={`ceia-extra-remove-${x.id}`} aria-label="remove"
                          className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <input value={saveName} onChange={(e) => setSaveName(e.target.value)} maxLength={60} placeholder={t("ceia.save_name_ph")} data-testid="ceia-save-name-input"
                  className="w-64 rounded-full border border-xmas-gold/30 bg-xmas-navy/50 px-4 py-3 text-xs text-xmas-snow placeholder:text-xmas-slate focus:border-xmas-gold focus:outline-none" />
                <button onClick={save} disabled={estimating} data-testid="ceia-save" className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-5 py-3 text-xs text-xmas-snow hover:border-xmas-gold disabled:opacity-50">
                  <Save className="h-4 w-4" /> {t("ceia.save")}
                </button>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("🔗"); }} className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-5 py-3 text-xs text-xmas-snow hover:border-xmas-gold">
                <Share2 className="h-4 w-4" /> {t("cta.share")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved dinner snapshot items modal (rendered first so it stays below the recipe/swap modals) */}
      <Modal open={!!saveView} onClose={() => { setSaveView(null); setSavedDirty(false); }} title={saveView ? `${saveView.name || t("ceia.saved_items")} · ${fmtDate(saveView.saved_at)}` : ""} testid="ceia-save-view-modal">
        {saveView && (
          <div>
            <div className="mb-4 flex flex-wrap gap-4 text-sm text-xmas-slate">
              <span><b className="font-mono-num text-xmas-snow">{saveView.guests}</b> {t("ceia.guests")}</span>
              {saveView.budget ? <span className="font-mono-num text-xmas-champagne">{formatMoney(symbol, saveView.budget)}</span> : null}
              <span className="capitalize text-xmas-champagne">{styles.find((s) => s.id === saveView.style)?.name || saveView.style}</span>
            </div>
            <p className="mb-4 flex items-center gap-1.5 text-xs text-xmas-gold" data-testid="ceia-saved-tap-hint">
              <Info className="h-3.5 w-3.5" /> {t("ceia.saved_tap_hint")}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {(saveView.dishes || []).map((d, i) => {
                const recipe = recipes.find((rec) => rec.id === d.id);
                const qty = Number(d.qty || 1);
                return (
                  <div key={i} className="flex gap-3 rounded-xl bg-xmas-navy/40 p-3" data-testid="ceia-saved-item">
                    <button type="button" onClick={() => { if (recipe) setRecipeModal(recipe); }} disabled={!recipe} className="flex min-w-0 flex-1 gap-3 text-left disabled:cursor-default">
                      {(recipe?.image || d.image) && <img src={recipe?.image || d.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wide text-xmas-champagne">{d.category === "extra" ? `${t("psearch.extra_label")}${d.store ? ` · ${d.store}` : ""}` : t(`ceia.course.${courseFor({ category: d.category })?.key || "main"}`)}</p>
                        <p className="text-sm text-xmas-snow">{d.name}</p>
                        {d.link && <a href={d.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-xmas-gold hover:underline">{t("prod.view")} <ExternalLink className="h-3 w-3" /></a>}
                      </div>
                    </button>
                    <div className="flex shrink-0 flex-col items-end justify-between gap-1">
                      <span className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 px-1.5 py-0.5 text-xs">
                        <button type="button" onClick={() => setSavedQty(i, Math.max(1, qty - 1))} disabled={qty <= 1} data-testid={`ceia-saved-qty-minus-${i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20 disabled:opacity-30"><Minus className="h-3 w-3" /></button>
                        <span className="font-mono-num min-w-[1.25rem] text-center text-xmas-snow" data-testid={`ceia-saved-qty-${i}`}>{qty}</span>
                        <button type="button" onClick={() => setSavedQty(i, qty + 1)} data-testid={`ceia-saved-qty-plus-${i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20"><Plus className="h-3 w-3" /></button>
                      </span>
                      <button type="button" onClick={() => removeSavedDish(i)} data-testid={`ceia-saved-remove-${i}`} title={t("saves.remove_item")} aria-label={t("saves.remove_item")} className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <ProductSearchBar compact historyKey="ceia" testid="ceia-saved-search" onSearch={(q) => openProductSearch(q, "saved")}
                maxLabel={saveView.budget && Number(saveView.budget) - dishesTotal(saveView.dishes) > 0 ? formatMoney(symbolForCurrency(config, saveView.estimate_currency || priceCurrency || user?.currency || "BRL"), Math.round((Number(saveView.budget) - dishesTotal(saveView.dishes)) * 100) / 100) : null} />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={() => loadSave(saveView)} data-testid="ceia-saved-load" className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">
                <Repeat className="h-4 w-4" /> {t("ceia.load_save")}
              </button>
              <button onClick={() => shareSavedWhatsApp(saveView)} data-testid="ceia-saved-share-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 px-5 py-2.5 text-xs font-semibold text-[#25D366] transition hover:bg-[#25D366]/20">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
            </div>
            {savedDirty && <SaveChangesBar t={t} onSave={saveSavedChanges} onDiscard={discardSavedChanges} testid="ceia-saved-changes" />}
          </div>
        )}
      </Modal>

      {/* Recipe modal */}
      {typicalMenu && <MenuShoppingList open={menuList} onClose={() => setMenuList(false)} title={typicalMenu.title} recipes={consoada} guests={guests} />}
      <Modal open={!!recipeModal} onClose={() => setRecipeModal(null)} title={recipeModal?.name} testid="ceia-recipe-modal">
        {recipeModal && (
          <div>
            <img src={recipeModal.image} srcSet={recipeSrcSet(recipeModal.image)} sizes="(max-width: 640px) 100vw, 640px" alt="" decoding="async" className="mb-4 h-44 w-full rounded-xl object-cover" />
            <p className="text-sm text-xmas-slate">{recipeModal.description}</p>
            <div className="mt-2 flex items-center gap-4 text-sm text-xmas-champagne">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {recipeModal.time_minutes} min</span>
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {recipeModal.servings} {t("ceia.guests")}</span>
            </div>
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-xmas-champagne">{t("recipes.ingredients")}</h4>
              <ul className="list-inside list-disc space-y-1 text-sm text-xmas-snow">
                {(recipeModal.ingredients || []).map((ing, i) => <li key={i}>{ing}</li>)}
              </ul>
            </div>
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-xmas-champagne">{t("recipes.steps")}</h4>
              <ol className="list-inside list-decimal space-y-1.5 text-sm text-xmas-snow">
                {(recipeModal.steps || []).map((st, i) => <li key={i}>{st}</li>)}
              </ol>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {!saveView && (
                <button onClick={() => { toggleSelect(recipeModal.id); setRecipeModal(null); }} data-testid="ceia-recipe-choose"
                  className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">
                  <Check className="h-4 w-4" /> {selected.includes(recipeModal.id) ? t("ceia.remove_dish") : t("ceia.choose_dish")}
                </button>
              )}
              {altsFor(recipeModal).length > 0 && (
                <button onClick={() => setSwapModal(recipeModal)} data-testid="ceia-recipe-swap"
                  className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-5 py-2.5 text-xs text-xmas-snow hover:border-xmas-gold">
                  <Repeat className="h-4 w-4" /> {t("ceia.see_others")}
                </button>
              )}
              <WhatsAppShareButton recipe={recipeModal} lang={lang} testid="ceia-recipe-share-whatsapp" />
            </div>
          </div>
        )}
      </Modal>

      {/* Swap dish modal — see other options for the same course */}
      <Modal open={!!swapModal} onClose={() => setSwapModal(null)} title={swapModal ? `${t("ceia.swap_title")} · ${swapModal.name}` : ""} testid="ceia-swap-modal">
        {swapModal && swapAlternatives.length === 0 && (
          <p className="text-sm text-xmas-slate" data-testid="ceia-swap-empty">{t("ceia.swap_empty")}</p>
        )}
        {swapModal && swapAlternatives.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {swapAlternatives.map((r) => (
              <div key={r.id} data-testid={`ceia-swap-option-${r.id}`} className="flex flex-col overflow-hidden rounded-xl border border-xmas-gold/20">
                <img src={r.image} srcSet={recipeSrcSet(r.image)} sizes="200px" alt="" loading="lazy" decoding="async" className="h-24 w-full object-cover" />
                <div className="flex flex-1 flex-col p-3">
                  <p className="text-sm font-semibold text-xmas-snow">{r.name}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-xmas-slate">{r.description}</p>
                  <button onClick={() => doSwap(r)} data-testid={`ceia-swap-pick-${r.id}`}
                    className="btn-gold mt-3 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wider">
                    <Repeat className="h-3.5 w-3.5" /> {t("ceia.swap_pick")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!prodModal} onClose={() => setProdModal(null)} title={prodModal?.label} testid="ceia-products-modal">
        <ProductResults loading={prodModal?.loading} result={prodModal?.result} onChoose={chooseExtra} />
      </Modal>
    </div>
  );
}


